import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { getRecoil } from "recoil-nexus";
import Bugsnag from "@bugsnag/js";
import { consola } from "consola";
import organizationIdSelector from "../atoms/organizationIdSelector";
import { getUserIdToken } from "../utils/auth";

const API_BASE_URL = "https://app.mighty45.com/api/v1";

function mapApiKeyDoc(doc) {
  const data = doc.data();
  const isLegacy = data.version !== 2 && Boolean(data.key);
  return {
    id: doc.id,
    ...data,
    isLegacy,
    displayKey: isLegacy
      ? data.key
      : `${data.keyPrefix || "m45_********"}…`,
    status: data.status || (isLegacy ? "active" : data.status || "unknown"),
  };
}

const useAPIKeys = () => {
  const organizationId = getRecoil(organizationIdSelector);
  const [keys, setKeys] = React.useState([]);
  const [lastCreatedSecret, setLastCreatedSecret] = React.useState(null);

  const addAPIKey = React.useCallback(
    async function addAPIKey({ label = "API key", expiresInDays = null } = {}) {
      try {
        const token = await getUserIdToken();
        const body = { label };
        if (expiresInDays === null || expiresInDays === 0) {
          body.expiresInDays = null;
        } else {
          body.expiresInDays = Number(expiresInDays);
        }

        const response = await fetch(`${API_BASE_URL}/organization/api-keys`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || `HTTP ${response.status}`);
        }

        setLastCreatedSecret(payload.data);
        return payload.data;
      } catch (e) {
        Bugsnag.notify(e);
        throw e;
      }
    },
    [],
  );

  const revokeAPIKey = React.useCallback(
    async function revokeAPIKey(keyId) {
      try {
        const token = await getUserIdToken();
        const response = await fetch(
          `${API_BASE_URL}/organization/api-keys/${encodeURIComponent(keyId)}/revoke`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || `HTTP ${response.status}`);
        }
      } catch (e) {
        Bugsnag.notify(e);
        throw e;
      }
    },
    [],
  );

  const deleteAPIKey = React.useCallback(
    async function deleteAPIKey(keyRef) {
      const ref = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("apiKeys")
        .doc(keyRef);

      try {
        await ref.delete();
      } catch (e) {
        Bugsnag.notify(e);
        throw e;
      }
    },
    [organizationId],
  );

  const clearLastCreatedSecret = React.useCallback(() => {
    setLastCreatedSecret(null);
  }, []);

  React.useEffect(() => {
    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("apiKeys");

    const subscription = ref.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> useAPIKeys");
      const data = snapshot.docs.map(mapApiKeyDoc);
      setKeys(data);
    });

    return () => {
      subscription();
    };
  }, [organizationId]);

  return {
    keys,
    addAPIKey,
    revokeAPIKey,
    deleteAPIKey,
    lastCreatedSecret,
    clearLastCreatedSecret,
  };
};

export default useAPIKeys;
